import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { featureAllocationsApi } from '@/utils/api';
import type { Program, Team } from '@/types';

// Feature Allocation type matching backend schema
export interface FeatureAllocation {
  id: string;
  featureId: string;
  programId: string;
  teamId?: string;
  teamWeeks: number;
  scenarioId?: string;
  quarter?: number;
  createdAt: string;
  updatedAt: string;
  program?: Program;
  team?: Team;
  feature?: {
    id: string;
    name: string;
    estimatedTeamWeeks?: number;
  };
  scenario?: {
    id: string;
    name: string;
  };
}

interface FeatureAllocationsState {
  allocationsByFeatureId: Record<string, FeatureAllocation[]>;
  loadingFeatureIds: string[];
  creatingForFeatureId: string | null;
  updatingAllocationIds: string[];
  deletingAllocationIds: string[];
  error: string | null;
}

const initialState: FeatureAllocationsState = {
  allocationsByFeatureId: {},
  loadingFeatureIds: [],
  creatingForFeatureId: null,
  updatingAllocationIds: [],
  deletingAllocationIds: [],
  error: null,
};

// API response types
interface AllocationsApiResponse {
  allocations?: FeatureAllocation[];
  allocation?: FeatureAllocation;
}

// Async thunks
export const fetchFeatureAllocations = createAsyncThunk(
  'featureAllocations/fetchByFeature',
  async (featureId: string, { rejectWithValue }) => {
    const response = await featureAllocationsApi.getAll({ featureId });
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch feature allocations');
    }
    const data = response.data as AllocationsApiResponse | FeatureAllocation[] | undefined;
    const allocations = Array.isArray(data) ? data : (data?.allocations || []);
    return { featureId, allocations };
  }
);

export const createFeatureAllocation = createAsyncThunk(
  'featureAllocations/create',
  async (
    data: {
      featureId: string;
      programId: string;
      teamId?: string;
      teamWeeks: number;
      scenarioId?: string;
      quarter?: number;
    },
    { rejectWithValue }
  ) => {
    const response = await featureAllocationsApi.create(data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to create feature allocation');
    }
    const respData = response.data as AllocationsApiResponse | FeatureAllocation | undefined;
    const allocation = (respData && 'allocation' in respData) ? respData.allocation : respData as FeatureAllocation | undefined;
    return { featureId: data.featureId, allocation };
  }
);

export const updateFeatureAllocation = createAsyncThunk(
  'featureAllocations/update',
  async (
    { id, featureId, data }: {
      id: string;
      featureId: string;
      data: { teamWeeks?: number; teamId?: string; scenarioId?: string; quarter?: number }
    },
    { rejectWithValue }
  ) => {
    const response = await featureAllocationsApi.update(id, data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update feature allocation');
    }
    const respData = response.data as AllocationsApiResponse | FeatureAllocation | undefined;
    const allocation = (respData && 'allocation' in respData) ? respData.allocation : respData as FeatureAllocation | undefined;
    return { featureId, allocation };
  }
);

export const deleteFeatureAllocation = createAsyncThunk(
  'featureAllocations/delete',
  async ({ id, featureId }: { id: string; featureId: string }, { rejectWithValue }) => {
    const response = await featureAllocationsApi.delete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to delete feature allocation');
    }
    return { id, featureId };
  }
);

const featureAllocationsSlice = createSlice({
  name: 'featureAllocations',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearFeatureAllocations(state, action: PayloadAction<string>) {
      delete state.allocationsByFeatureId[action.payload];
    },
  },
  extraReducers: (builder) => {
    // Fetch feature allocations
    builder
      .addCase(fetchFeatureAllocations.pending, (state, action) => {
        state.loadingFeatureIds.push(action.meta.arg);
        state.error = null;
      })
      .addCase(fetchFeatureAllocations.fulfilled, (state, action) => {
        state.loadingFeatureIds = state.loadingFeatureIds.filter(
          (id) => id !== action.payload.featureId
        );
        state.allocationsByFeatureId[action.payload.featureId] = action.payload.allocations;
      })
      .addCase(fetchFeatureAllocations.rejected, (state, action) => {
        state.loadingFeatureIds = state.loadingFeatureIds.filter((id) => id !== action.meta.arg);
        state.error = action.payload as string || 'Failed to fetch feature allocations';
      });

    // Create feature allocation
    builder
      .addCase(createFeatureAllocation.pending, (state, action) => {
        state.creatingForFeatureId = action.meta.arg.featureId;
        state.error = null;
      })
      .addCase(createFeatureAllocation.fulfilled, (state, action) => {
        state.creatingForFeatureId = null;
        const { featureId, allocation } = action.payload;
        if (!state.allocationsByFeatureId[featureId]) {
          state.allocationsByFeatureId[featureId] = [];
        }
        if (allocation) {
          state.allocationsByFeatureId[featureId].push(allocation);
        }
      })
      .addCase(createFeatureAllocation.rejected, (state, action) => {
        state.creatingForFeatureId = null;
        state.error = action.payload as string || 'Failed to create feature allocation';
      });

    // Update feature allocation
    builder
      .addCase(updateFeatureAllocation.pending, (state, action) => {
        state.updatingAllocationIds.push(action.meta.arg.id);
        state.error = null;
      })
      .addCase(updateFeatureAllocation.fulfilled, (state, action) => {
        const { featureId, allocation } = action.payload;
        state.updatingAllocationIds = state.updatingAllocationIds.filter(
          (id) => id !== action.meta.arg.id
        );
        if (allocation) {
          const allocations = state.allocationsByFeatureId[featureId];
          if (allocations) {
            const index = allocations.findIndex((a) => a.id === allocation.id);
            if (index !== -1) {
              allocations[index] = allocation;
            }
          }
        }
      })
      .addCase(updateFeatureAllocation.rejected, (state, action) => {
        state.updatingAllocationIds = state.updatingAllocationIds.filter(
          (id) => id !== action.meta.arg.id
        );
        state.error = action.payload as string || 'Failed to update feature allocation';
      });

    // Delete feature allocation
    builder
      .addCase(deleteFeatureAllocation.pending, (state, action) => {
        state.deletingAllocationIds.push(action.meta.arg.id);
        state.error = null;
      })
      .addCase(deleteFeatureAllocation.fulfilled, (state, action) => {
        const { id, featureId } = action.payload;
        state.deletingAllocationIds = state.deletingAllocationIds.filter(
          (allocId) => allocId !== id
        );
        const allocations = state.allocationsByFeatureId[featureId];
        if (allocations) {
          state.allocationsByFeatureId[featureId] = allocations.filter((a) => a.id !== id);
        }
      })
      .addCase(deleteFeatureAllocation.rejected, (state, action) => {
        state.deletingAllocationIds = state.deletingAllocationIds.filter(
          (id) => id !== action.meta.arg.id
        );
        state.error = action.payload as string || 'Failed to delete feature allocation';
      });
  },
});

export const { clearError, clearFeatureAllocations } = featureAllocationsSlice.actions;

export default featureAllocationsSlice.reducer;
